/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useContext, useEffect, useState } from 'react';
import { Button, Modal } from '@scuf/common';
import UtilityAccountAction from '../../../context/UtilityAccountContext/UtilityAccountActions';
import { useUtilityAccountContext } from '../../../context/UtilityAccountContext/UtilityAccountContext';
import './MeterNameChangeTheme.scss';
import descriptors from '../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { ICommonProps } from '../../UtilityServiceModal/IUtilityService';
import { DataTable } from '@scuf/datatable';
import { ICellData } from '@scuf/datatable/dist/components/DataTable/IDataTableInterfaces';
import MeterNameCell from './MeterNameCell/MeterNameCell';
import withLoadingStatus from '../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../shared/StatusWrappers/ErrorStatus';
import { ApiContext } from '../../../context/ApiContext';
import { getMeterNameChangeNotifyInfo } from '../../shared/Utils/UtilitySettings/UtilitySettingsUtils';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  IMeterRequestContext,
  useConnectorForMeters
} from './utils/useConnectorForMeters';

interface IMeterData {
  meterId: string;
  meterName: string;
}

interface IMeterListResponseData {
  data: IMeterData[];
}

interface IMeterNameChangeTableContentProps {
  data: IMeterData[];
  providerId: string;
  onRefresh: (silentRefresh?: boolean) => Promise<void>;
  requestContext: IMeterRequestContext;
}

export const MeterNameChangeTableContent = (
  props: IMeterNameChangeTableContentProps
) => {
  // Here we'll do put request

  const { data, onRefresh, providerId, requestContext } = props;
  const [currSubmittingMeterId, setCurrSubmittingMeterId] = useState<string>();
  const [currEditingMeter, setCurrEditingMeter] = useState<string>('');
  const apiContext = useContext(ApiContext);
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { notifyMessage } = apiContext;

  const connectorConfig = {
    name: 'utility-provider-update-meter-name'
  };

  const dataConnector = useConnectorForMeters({
    connectorConfig,
    requestContext
  });

  const { status, loading, error } = dataConnector.data;

  const onSubmit = async (meterId: string, newMeterName: string) => {
    setCurrSubmittingMeterId(meterId);
    // PUT Request
    const result: IAny = await dataConnector.actions.update('', {
      meterId,
      meterName: newMeterName,
      siteId: requestContext.context.siteId,
      serviceProviderId: requestContext.context.providerId
    });

    // Snackbar Notification Message
    const { notifyDescription, notifyType } = getMeterNameChangeNotifyInfo(
      result
    );
    notifyMessage &&
      notifyMessage(
        t(notifyDescription, { meterId, meterName: newMeterName }),
        notifyType
      );
    await onRefresh(notifyType !== 'error');
    setCurrSubmittingMeterId('');
  };

  useEffect(() => {
    if (!loading && (status || error)) {
    }
  }, [loading, status, error]);

  const renderMeterNameCell = (celldata: ICellData) => {
    const { meterId, meterName } = celldata.rowData;
    return (
      <MeterNameCell
        meterId={meterId}
        meterName={meterName}
        onSubmit={onSubmit}
        isSubmitting={currSubmittingMeterId === meterId && loading}
        currEditingMeter={currEditingMeter}
        setCurrEditingMeter={setCurrEditingMeter}
      />
    );
  };

  return (
    <DataTable data={data} className="meterlist-datatable">
      <DataTable.Column
        initialWidth="55%"
        field="meterId"
        header={t(d.METERID)}
      />
      <DataTable.Column
        initialWidth="45%"
        field="meterName"
        header={t(d.METERNAME)}
        renderer={renderMeterNameCell}
      />
    </DataTable>
  );
};

const MeterNameChangeTableContentWithLoader = withLoadingStatus(
  withErrorStatus(MeterNameChangeTableContent)
);

function MeterNameChange(props: ICommonProps) {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();

  const [state, dispatch] = useUtilityAccountContext();
  const { requestContext } = props;

  let {
    showMeterNameChangeModal,
    selectedUtility: {
      accountNumber,
      providerName,
      id,
      providerId,
      utilityType,
      status: selectedUtilityStatus
    }
  } = state;
  // Set values in options to populate get parameter
  const connectorConfig = {
    name: 'utility-provider-list-meters',
    options: {
      serviceProviderId: providerId,
      accountNumber: accountNumber,
      utilityAccountId: id
    }
  };

  const newReqContext: IMeterRequestContext = {
    ...requestContext,
    context: { ...requestContext.context, providerId }
  };

  const [isModalOpen, setIsModalOpen] = useState<boolean>(
    showMeterNameChangeModal
  );

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => {
      dispatch(UtilityAccountAction.resetSelectedUtilityInfo());
      dispatch(UtilityAccountAction.hideProviderModal());
    }, 100);
  };

  const dataConnector = useConnectorForMeters({
    connectorConfig,
    requestContext: newReqContext
  });

  const { one, status, loading, error } = dataConnector.data;

  const [isLoading, setIsLoading] = useState<boolean>(loading || true);
  const [isSilentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState([] as IMeterData[]);

  const getMeterListData = async () => {
    const res = await dataConnector.actions.getOne('');
    const meterList = (res as unknown) as IMeterListResponseData;
    if (meterList.data) {
      setData(meterList.data);
    }
  };

  const onRefresh = async (silentRefresh: boolean = false) => {
    setIsSilentLoading(silentRefresh);
    await getMeterListData();
    setIsSilentLoading(false);
  };

  useEffect(() => {
    if (showMeterNameChangeModal) {
      setIsLoading(loading);
      if (!loading && (status || error)) {
        setErrorInfo(error);
        setStatusCode(status);
      }
    }
  }, [one, loading, status, showMeterNameChangeModal]);

  useEffect(() => {
    if (showMeterNameChangeModal) {
      setIsModalOpen(true);
      getMeterListData();
    } else {
      setData([] as IMeterData[]);
    }
  }, [showMeterNameChangeModal]);

  return (
    <Modal
      className={`meter-name-change ${
        isModalOpen ? 'slide-in-from-right' : 'slide-out-to-right'
      }`}
      size="small"
      onClose={closeModal}
      open={showMeterNameChangeModal}
      closeOnDimmerClick={false}
    >
      <div className="modal-header">
        <p className="header-text">
          {`${providerName} - ${utilityType} (${accountNumber})`}
        </p>
        <div className="modal-header-description">
          {t(d.METERNAMECHANGEDESCRIPTION)}
        </div>
        {data && (
          <p className="meter-list-count">{`${data.length ||
            0} Total Meters`}</p>
        )}
      </div>

      <Modal.Content className="meter-list-modal-content" scrolling>
        <MeterNameChangeTableContentWithLoader
          loading={isLoading}
          error={errorInfo}
          statusCode={statusCode}
          providerId={providerId}
          data={data}
          requestContext={newReqContext}
          onRefresh={(slentRefresh: boolean = false) => onRefresh(slentRefresh)}
          connectionErrorMessage={t(d.CONNECTIONERROR)}
          loadingText={t(d.LOADING)}
          noDataMessage={t(d.NODATA)}
          // silentLoading={isSilentLoading} // Not Working
        />
      </Modal.Content>
      <Modal.Footer className="meter-name-change-modal-footer">
        <Button
          type="secondary"
          content={t(d.CLOSE)}
          onClick={closeModal}
        ></Button>
      </Modal.Footer>
    </Modal>
  );
}
export default MeterNameChange;
